// クラシック版の「拡大して見る」ボタン btnSubZoom（段階 C の2 の 5d。2026-10-25 に chibi.js と画面ファイル gen1.js・gen1b.js・
// origin.js・jakigan.js・boys.js・storm.js・run.js・genesis.js・dressup.js・portrait.js の 11 通りから描き直した。動きは前と同じ）。
// 押すたびに段（POS: SC 倍率・TOP 上下のずらし・RESET 保存の前に戻すか）を順に回し、作っているキャラと名前の文字を拡大・移動する（d3 の動き 250ms・easeCircle）。
// 11 通りの違いは次の 4 つだけだったので設定にした（どれもページの chibiConfig。既定は画面の系統 SCREEN で決まる）:
//   ZOOM_POS            段の表。既定: エンジンのまま・第2世代 [等倍, 2.5 倍 2 つ]、第1世代 a と CHOCO・mono（ZOOM_FRONT_TYPE: false）は bowl の 4 段、第1世代 b（front 型）は 3 段
//   ZOOM_MOBILE         スマホのとき起動で書きかえる段。{ i } は 1×0.937 倍・-7.5×(1-0.937)、{ i, sc, top } は sc×0.937・top×0.937、{ i, fill, top } はメインの高さ ÷（幅×fill）・top
//   ZOOM_RESET_POS      保存の前に戻したあとの段の番号（エンジンのまま・第2世代 1、第1世代 0。1 や 5 は段が無い・2 段目のことがあり、次に押すと飛ぶ。前のまま）
//   ZOOM_RESET_TEXT_ONLY 戻すとき名前の文字は台詞のメーカー（MODE_TEXT）だけ戻すか（エンジンのまま・第2世代は真、front 型は偽）
import { chibi } from './engine.js';
import { screen } from './colors.js';
import * as fx from './fx.js';

type Engine = Record<string, any>;
const E = (): Engine => chibi() as unknown as Engine;
interface ZoomPos {
  SC: number;
  TOP: number;
  RESET: boolean;
  CLIP: unknown;
}
interface ZoomMobile {
  i: number;
  sc?: number;
  top?: number;
  fill?: number;
}
const pos = (SC: number, TOP: number, RESET: boolean): ZoomPos => ({ SC, TOP, RESET, CLIP: null });

/** ボタンの定義を作る（設定は読み込みの時点で決める。段の表はページの値をそのまま使い、スマホの書きかえもそこに書く＝前と同じ） */
export function makeZoomButton(): Record<string, unknown> {
  const e = E(), scr = screen();
  const front = scr === 'gen1b' && e.config('ZOOM_FRONT_TYPE', true);
  const bowl = (scr === 'gen1a' || scr === 'gen1b') && !front;
  const defPos = front
    ? [pos(1, 0, false), pos(1.5, -0.444, false), pos(3, -0.6, true)]
    : bowl
      ? [pos(1, 0, false), pos(2.2, -0.25, true), pos(1.3, -0.4, true), pos(2.4, -0.75, true)]
      : [pos(1, 0, false), pos(2.5, -0.2, true), pos(2.5, -1.0, true)];
  const defMobile: ZoomMobile[] = front ? [{ i: 0 }, { i: 1, sc: 1.5, top: -0.507 }] : bowl ? [{ i: 0 }] : [];
  const mobile = e.config('ZOOM_MOBILE', defMobile) as ZoomMobile[];
  const resetPos = e.config('ZOOM_RESET_POS', scr === 'gen1a' || scr === 'gen1b' ? 0 : 1);
  const textOnly = e.config('ZOOM_RESET_TEXT_ONLY', scr === 'new' || scr === 'gen2');
  // 今の段の、作るキャラ（と名前の文字）の transform を返す関数。段の倍率・ずらしは呼んだ時点で取り、キャラの幅・高さは動きが始まるときに読む（d3 に関数を渡す。前と同じ）
  const transformOf = (): (() => string) => {
    const z = E().btnSubZoom;
    const p = z.POS[z.EXPOS];
    const xsc = p.SC, ysc = p.SC, ypos = p.TOP;
    return () => {
      const en = E();
      const w = en.svgMake.attr('width'), h = en.svgMake.attr('height');
      return 'translate(' + (w * xsc - w) * -(en.SCALE_MAKE / 2) + ',' + +((h * ysc - h) * ypos) + '),scale(' + xsc + ',' + ysc + ')';
    };
  };
  return {
    ID: e.ATTR_EDITSUBBUTTON + 'Zoom',
    STATUS: 0,
    FILE: [e.PATH_IMG + 'icon_left/view.svg', e.PATH_IMG + 'icon_left/view.svg'],
    POS: e.config('ZOOM_POS', defPos),
    // 起動: スマホなら段を書きかえ、末尾の次から EXEC して最初の段にする
    INIT: () => {
      const z = E().btnSubZoom;
      const a = E().isMobile ? 0.937 : 1;
      if (E().isMobile) {
        mobile.forEach((m) => {
          if (m.fill !== undefined) {
            z.POS[m.i].SC = fx.height(E().DIV_F_MAIN) / (fx.width(E().DIV_F_MAIN) * m.fill);
            z.POS[m.i].TOP = m.top;
          } else if (m.sc !== undefined) {
            z.POS[m.i].SC = m.sc * a;
            z.POS[m.i].TOP = m.top! * (1 * a);
          } else {
            z.POS[m.i].SC = 1 * a;
            z.POS[m.i].TOP = -7.5 * (1 - a);
          }
        });
      }
      z.EXPOS = z.POS.length;
      z.EXEC();
    },
    EXPOS: 0,
    CLICK: () => {
      E().btnSubZoom.EXEC();
    },
    // 次の段へ進めて、作っているキャラと名前の文字を拡大・移動する
    EXEC: () => {
      const z = E().btnSubZoom;
      if (++z.EXPOS >= z.POS.length) z.EXPOS = 0;
      E().svgMake.transition().duration(250).ease(fx.ease('circle')).attr('transform', transformOf());
      z.UPDATETEXT(250);
    },
    // 名前の文字（#chaSvgTextgname）を今の段に合わせる
    UPDATETEXT: (aDur: number) => {
      fx.d3Select('#' + E().SVG_TEXT + 'gname').transition().duration(aDur).ease(fx.ease('circle')).attr('transform', transformOf());
    },
    // 拡大をすぐ戻す（完成の前）
    RESET: () => {
      const en = E();
      en.btnSubZoom.EXPOS = resetPos;
      en.svgMake.attr('transform', 'translate(0, 0), scale(1, 1)');
      if (!textOnly || en.MODE_TEXT) fx.d3Select('#' + en.SVG_TEXT + 'gname').attr('transform', 'translate(0, 0), scale(1, 1)');
    },
    // 今の段が拡大中（保存の前に戻すもの）か
    IS_RESET: () => E().btnSubZoom.POS[E().btnSubZoom.EXPOS].RESET,
    CLIP: () => E().btnSubZoom.POS[E().btnSubZoom.EXPOS].CLIP,
  };
}
